import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  ExplorerService,
  ModalConfig,
} from "@app/modules/shared/services/explorer.service";
import { DynamicComponent } from "@app/data/dashboard/interfaces/dynamic-component.interface";
import { ApiService } from "@app/modules/shared/services/base/api.service";
import { KlSweetAlertService } from "@app/modules/shared/services/message/kl-sweet-alert.service";
import { UtilService } from "@app/modules/shared/services/util.service";
import { ToastService } from "@app/modules/shared/services/message/toast.service";
import { TranslateService } from "@app/modules/shared/services/translate/translate.service";
import { ConvertIsoDateService } from "@app/modules/shared/services/convert-iso-date.service";
import { KlLoadingService } from "@app/modules/shared/services/kl-loading.service";
import { BaseForm } from "@app/data/shared/models/base-form.interface";
import { of, Subscription } from "rxjs";
import { CurrentAccountListComponent } from "@dashboard/modules/finance-management/current-account/components/current-account-list/current-account-list.component";
import { InstallationUserDto } from "@dashboard/modules/management/installation-user/model/installation-user.dto";

@Component({
  selector: "kl-installation-user-form",
  templateUrl: "./installation-user-form.component.html",
  styleUrls: ["./installation-user-form.component.scss"],
})
export class InstallationUserFormComponent
  implements OnInit, AfterViewInit, OnDestroy, BaseForm
{
  @Input() isReadOnly: boolean;
  @Input() tabId: string;
  @Input() listTabId: string;
  @Input() modalId: string;
  @Input() mode: string;
  @Input() entity: InstallationUserDto = new InstallationUserDto();
  @ViewChild("installationUserForm", { static: true })
  private installationUserForm: any;
  @ViewChild("focusElement", { static: true }) private elementFocus: ElementRef;
  title = "";
  apiError: boolean;
  validationError: boolean;
  alertType: string;
  alertMessage: any;
  isOpenFirstAccordion = true;
  subscription: Subscription;

  constructor(
    private apiService: ApiService,
    private sweetAlert: KlSweetAlertService,
    private utilService: UtilService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private dateConvert: ConvertIsoDateService,
    private explorerService: ExplorerService,
    private klLoadingService: KlLoadingService,
    private cd: ChangeDetectorRef
  ) {
    this.subscription = explorerService.transferState$.subscribe((state) => {
      this.formChange(true);
      if (state.state === "INSTALLATION_USER_CURRENT_ACCOUNT_CURRENT_ITEM") {
        if (state.payload) {
          if (state.payload.status === "ACTIVE") {
            this.entity.current_account = state.payload;
          } else {
            this.handleError("current_account_is_closed_to_use");
          }
        }
        this.cd.markForCheck();
      }
    });
  }

  ngOnInit() {
    this.klLoadingService.hide();
    switch (this.mode) {
      case "add":
        this.entity.status = "ACTIVE";
        this.title =
          this.translateService.instant("new") +
          " " +
          this.translateService.instant("installation_user");
        break;
      case "edit":
      case "view":
        this.title = this.translateService.instant("installation_user") + " - ";
        break;
    }
  }

  ngAfterViewInit() {
    this.elementFocus.nativeElement.focus();
    this.cd.markForCheck();
  }

  formChange(event) {
    if (this.tabId) {
      this.explorerService.changeFormStatus({
        tabId: this.tabId,
        changeForm: event,
      });
    }
  }

  setModal() {
    const modalConfig = <ModalConfig>{
      title: "current_accounts",
      stateSelector: "INSTALLATION_USER_CURRENT_ACCOUNT",
      childTemplate: <DynamicComponent>{
        mode: "DIALOG",
        modalId: "02.01.01",
        dblSelect: true,
        template: CurrentAccountListComponent,
      },
    };
    this.explorerService.openModal(modalConfig);
  }

  commitSave(action?: string) {
    if (this.installationUserForm.valid && this.entity.current_account) {
      this.klLoadingService.show("saving");
      switch (action) {
        case "SAVE":
          switch (this.mode) {
            case "add":
              this.apiService
                .addData("installation-user", this.entity)
                .subscribe(
                  (response) => this.handleResponse(response),
                  (error) => this.handleError(error)
                );
              break;
            case "edit":
              this.apiService
                .updateData("installation-user", this.entity)
                .subscribe(
                  (response) => this.handleResponse(response),
                  (error) => this.handleError(error)
                );
              break;
          }
          break;
        case "SAVE_NEW":
          this.apiService.addData("installation-user", this.entity).subscribe(
            (response) => this.handleNew(response),
            (error) => this.handleError(error)
          );
          break;
      }
    } else {
      this.handleError("required_fields_must_be_entered", "validationError");
    }
  }

  handleResponse(response) {
    this.formChange(false);
    this.klLoadingService.hide();
    this.toastService.toastSuccess();
    if (this.tabId) {
      this.explorerService.closeTab(this.tabId);
    }
    if (this.modalId) {
      this.explorerService.closeModal(this);
    }
    if (this.listTabId) {
      this.explorerService.setRefreshEndProsseses(this.listTabId);
    }
    this.cd.markForCheck();
  }

  handleNew(response) {
    this.formChange(false);
    this.klLoadingService.hide();
    this.toastService.toastSuccess();
    this.clearForm();
    this.cd.markForCheck();
  }

  handleError(error, errorType = "apiError") {
    this[errorType] = true;
    this.alertType = "error";
    this.alertMessage = this.utilService.alertErrorMessage(error);
    this.klLoadingService.hide();
    this.cd.markForCheck();
    return of(null);
  }

  unlockEntity() {
    this.apiService.unlockData("installation-user", this.entity.id).subscribe();
  }

  clearForm() {
    this.apiError = false;
    this.validationError = false;
    Object.keys(this.entity).forEach((key) => {
      delete this.entity[key];
    });
    this.entity.status = "ACTIVE";
    this.elementFocus.nativeElement.focus();
  }

  clearCurrentAccount() {
    this.formChange(true);
    this.entity.current_account = null;
  }

  ngOnDestroy() {
    if (this.mode === "edit") {
      this.unlockEntity();
    }
    this.subscription.unsubscribe();
  }
}
